import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import DataGrid, { Column, Pager, Paging, Scrolling } from "devextreme-react/data-grid";
import axios from "axios";
import { Button as DevXButton } from "devextreme-react/button";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

interface InwartReportModalProps {
  storeId: string;
  materialType: string;
  isMemo: boolean;
  onClose: () => void;
}

const InwartReportModal: React.FC<InwartReportModalProps> = ({
  storeId,
  materialType,
  isMemo,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<any[]>([]);
  const dataGridRef = useRef<any>(null);

  useEffect(() => {
    if (!storeId || !materialType) return;

    const fetchDetails = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          "https://mayaveposapi.sdaemon.com/api/v1/Report/GetStoreInwardStockItemDetail",
          {
            params: {
              StoreId: storeId,
              MaterialType: materialType,
              IsMemo: isMemo,
            },
          }
        );
        setDetails(res.data || []);
      } catch (err) {
        console.error("Error fetching modal data:", err);
        setDetails([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [storeId, materialType, isMemo]);

  const handleExportExcel = useCallback(() => {
    if (!details.length) {
      console.warn("No data to export");
      return;
    }

    setLoading(true);

    try {
      const columns = [
        { key: "storeName", name: "Store Name" },
        { key: "storeCode", name: "Store Code" },
        { key: "materialType", name: "Material Type" },
        { key: "category", name: "Category" },
        { key: "metalKT", name: "Metal KT" },
        { key: "size", name: "Size" },
        { key: "colourCode", name: "Colour Code" },
        { key: "bagNo", name: "Bag No." },
        { key: "styleNo", name: "Style No" },
        { key: "orderNo", name: "Order No" },
        { key: "collectionName", name: "Collection" },
        { key: "complexity", name: "Complexity" },
        { key: "huid", name: "HUID" },
        { key: "invoiceDate", name: "Invoice Date" },
        { key: "pcs", name: "Quantity" },
        { key: "diaPcs", name: "Dia Qty" },
        { key: "stnPcs", name: "STN Qty" },
        { key: "grossWt", name: "Gross Wt" },
        { key: "netWt", name: "Net Wt" },
        { key: "diaWt", name: "Dia Wt" },
        { key: "colorStoneWt", name: "Color Stone Wt" },
        { key: "purchaseRate", name: "Purchase Rate" },
        { key: "totalCostAmt", name: "Total Cost Amt" },
        { key: "todaysGoldRate", name: "Today's Gold Rate" },
      ];

      const excelData = details.map((item) =>
        columns.reduce(
          (obj, col) => ({
            ...obj,
            [col.name]:
              col.key === "invoiceDate" && item[col.key]
                ? new Date(item[col.key]).toLocaleDateString()
                : item[col.key] ?? "",
          }),
          {}
        )
      );

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      worksheet["!cols"] = columns.map((col) => ({
        width: col.name.length + 5,
      }));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Inward Report");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAs(
        new Blob([excelBuffer], { type: "application/octet-stream" }),
        `Inward_Report_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
    } catch (err) {
      console.error("Excel export error:", err);
    } finally {
      setLoading(false);
    }
  }, [details]);


  return (
    <Modal show onHide={onClose} size="xl" backdrop='static'>
      <Modal.Header closeButton>
        <Modal.Title>View Inward Stock</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-2 d-flex align-items-center justify-content-end">
          <DevXButton
            icon="https://img.favpng.com/18/7/13/scalable-vector-graphics-microsoft-excel-clip-art-portable-network-graphics-png-favpng-yynmZdMbFF2A9c7tcbZa6Ewv5.jpg"
            hint="Export to Excel"
            onClick={handleExportExcel}
          />
        </div>

        {loading ? (
          <div className="text-center my-4">
            <Spinner animation="border" />
          </div>
        ) : (
          <DataGrid
            ref={dataGridRef}
            dataSource={details}
            columnAutoWidth={true}
            allowColumnResizing={true}
            // rowAlternationEnabled={true}
            noDataText="No details found."
          >
            <Paging defaultPageSize={10} />
            <Pager
              showPageSizeSelector={true}
              allowedPageSizes={[10, 25, 50, 100]}
              showInfo={true}
            />
            <Scrolling
              mode="standard"
              useNative={true}
              scrollByContent={true}
              scrollByThumb={true}
            />
            <Column dataField="storeName" caption="Store Name" />
            <Column dataField="storeCode" caption="Store Code" />
            <Column dataField="materialType" caption="Material Type" />
            <Column dataField="category" caption="Category" />
            <Column dataField="metalKT" caption="Metal KT" />
            <Column dataField="size" caption="Size" />
            <Column dataField="colourCode" caption="Colour Code" />
            <Column dataField="bagNo" caption="BagNo." />
            <Column dataField="styleNo" caption="Style No" />
            <Column dataField="orderNo" caption="Order No" />
            <Column dataField="collectionName" caption="Collection" />
            <Column dataField="complexity" caption="Complexity" />
            <Column dataField="huid" caption="HUID" />
            <Column dataField="invoiceDate" caption="Invoice Date" dataType="date" />
            <Column dataField="pcs" caption="Quantity" />
            <Column dataField="diaPcs" caption="Dia Qty" />
            <Column dataField="stnPcs" caption="STN Qty" />
            <Column dataField="grossWt" caption="Gross Wt" format="#,##0.000" />
            <Column dataField="netWt" caption="Net Wt" format="#,##0.000" />
            <Column dataField="diaWt" caption="Dia Wt" format="#,##0.000" />
            <Column dataField="colorStoneWt" caption="Color Stone Wt" format="#,##0.000" />
            <Column dataField="purchaseRate" caption="Purchase Rate" format="#,##0.00" />
            <Column dataField="totalCostAmt" caption="Total Cost Amt" format="#,##0.00" />
            <Column dataField="todaysGoldRate" caption="Today's Gold Rate" format="#,##0.00" />
          </DataGrid>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default InwartReportModal;
